import { Dispatch, SetStateAction } from "react";
import { CoolingMethodOption } from "../config/enums/IngredientType.enum";
import { CompanyOption, UserProfile } from "../types/auth.types";

export function deCamelize(
  text: string,
  options: {
    separator?: string;
    hasPreserveConsecutiveUppercase?: boolean;
  } = {}
): string {
  const { separator = "_", hasPreserveConsecutiveUppercase = false } = options;

  if (typeof text !== "string" || typeof separator !== "string") {
    throw new TypeError("The `text` and `separator` arguments should be of type `string`");
  }

  if (text.length < 2) {
    return hasPreserveConsecutiveUppercase ? text : text.toLowerCase();
  }

  const camelToSeparator = hasPreserveConsecutiveUppercase
    ? handlePreserveConsecutiveUppercase
    : regularDeCamelize;

  return camelToSeparator(text, separator);
}

export const getStringInsideBrackets = (input: any) => {
  const regex = /\(([^)]+)\)/;
  const result = input.match(regex);
  return result ? result[1] : null;
};

export function handlePreserveConsecutiveUppercase(deCamelize: string, separator: string): string {
  const firstPass = deCamelize.replace(
    /((?<![\p{Uppercase_Letter}\d])[\p{Uppercase_Letter}\d](?![\p{Uppercase_Letter}\d]))/gu,
    $0 => $0.toLowerCase()
  );

  return firstPass.replace(
    /(\p{Uppercase_Letter}+)(\p{Uppercase_Letter}\p{Lowercase_Letter}+)/gu,
    (_, $1, $2) => $1 + separator + $2.toLowerCase()
  );
}

function regularDeCamelize(deCamelize: string, separator: string): string {
  return deCamelize.replace(
    /([\p{Lowercase_Letter}\d])(\p{Uppercase_Letter})/gu,
    `$1${separator}$2`
  );
}

export function humanize(string: string): string {
  string = deCamelize(string);
  string = string
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
  string = string.charAt(0).toUpperCase() + string.slice(1);

  return string;
}

export type ResolvedCompany =
  | { status: true; id: string; name: string; tenantId: string }
  | { status: false; id: null; name: null; tenantId: null };

/**
 * The single implementation of company resolution: finds the company whose
 * tenant matches the signed-in user's tenant. Used by the auth redesign
 * (via `src/screens/auth/helpers/authSession.helpers.ts`) and, through
 * `checkAvailableUserCompanyId`, by the legacy Login/CognitoLogin screens.
 */
export const resolveUserCompany = (
  companies: CompanyOption[],
  userProfile: Pick<UserProfile, "tenantId">
): ResolvedCompany => {
  const match = companies.find(company => company.tenantId === userProfile.tenantId);
  if (!match) {
    return { status: false, id: null, name: null, tenantId: null };
  }
  return { status: true, id: match.id, name: match.title, tenantId: match.tenantId };
};

/** Kept, loosely typed, for the legacy Login/CognitoLogin screens. */
export const checkAvailableUserCompanyId = (
  company: any,
  userData: any
): { id: any; name: any; tenantId: any; status: boolean } =>
  resolveUserCompany(company ?? [], userData ?? {});

export const getCompanyLogo = (theme: any) => {
  switch (theme) {
    case "BLUE":
      return require("../assets/images/PoweredbyNature.png");
    default:
      return require("../assets/images/logo.png");
  }
};

export const hideSearchPath = (path: any) => {
  switch (path) {
    case "/auth":
      return false;
    case "/password-reset":
      return false;
    case "/password-forgot":
      return false;
    default:
      return true;
  }
};

export const valueGraterThanZero = (value: any) => {
  if (value > 0) {
    return true;
  } else {
    return false;
  }
};

export const valueIsZero = (value: any) => {
  if (value !== 0) {
    return true;
  } else {
    return false;
  }
};

export const isCheckPercentageIrrigation = (value: any) => {
  if (value > 100 || value < 0) {
    return false;
  } else {
    return true;
  }
};

export const validatePercentage = (value: any) => {
  if (value > 100 || value < 0) {
    return false;
  } else {
    return true;
  }
};

export const isValueValid = (value: any) => {
  if (value >= 0.1 && value <= 100) {
    return true;
  } else {
    return false;
  }
};

export const isAdminAuthorized = (user: any) => {
  if (["Admin", "Group Admin"].includes(user?.userRole[0])) {
    return true;
  }
  return false;
};

export const concatToNewArray = (values: Array<any>, propertyName: string) => {
  return [].concat.apply(
    [],
    values.map(f => f[propertyName])
  );
};

export const checkAuthorization = (
  rolePermissions: { [role: string]: string[] },
  userRole: string,
  userPermissions: string[]
) => {
  const allowedPermissions = rolePermissions[userRole];
  return (
    allowedPermissions &&
    allowedPermissions.some(permission => userPermissions.includes(permission))
  );
};

export const getCodeFromString = (str: string) => {
  return str.replace(/\s/g, "_").toLowerCase();
};

export function getLastTwentyYears(): any[] {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 21; i++) {
    years.push({
      key: i,
      text: (currentYear - i).toString(),
      value: (currentYear - i).toString()
    });
  }
  return years;
}

export const checkYearValidation = (data: number): boolean => {
  const currentYear = new Date().getFullYear();
  const year = data.toString();
  return year.length === 4 && data <= currentYear;
};

export const getHarvestYearValidationMessage = (value: unknown): string | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const yearString = String(value).trim();
  if (yearString.length !== 4 || Number.isNaN(Number(yearString))) {
    return "Harvest year must be a 4-digit year (for example, 2024).";
  }

  const year = parseInt(yearString, 10);
  const currentYear = new Date().getFullYear();

  if (year > currentYear) {
    return `Harvest year cannot be in the future. Please enter ${currentYear} or an earlier year.`;
  }

  return null;
};

export const customEmailValidation = (value: any) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+(\.[^\s@]+)*$/;
  if (!emailRegex.test(value)) {
    return "Please enter the valid email";
  }
  return true;
};

export const getPathName = () => {
  var currentPath = window.location.pathname;
  return currentPath;
};

export const generateRandomColors = (length: number): string[] =>
  Array.from(
    { length },
    () =>
      `#${Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, "0")}`
  );

export const getImpactChangePercentage = (originalImpact: number, simulatedImpact: number) => {
  return simulatedImpact
    ? parseFloat(
        Math.fround(((simulatedImpact - originalImpact) / originalImpact) * 100).toFixed(4)
      )
    : 0;
};

export const objectKeyValueList = (object: {
  [key: string]: string | number;
}): (string[] | number[])[] => {
  return Object.entries(object || {}).reduce(
    ([names, values]: [string[], number[]], [key, value]: any) => {
      names.push(key);
      values.push(value);
      return [names, values];
    },
    [[], []]
  );
};

// validate supplier contact number length max 20 characters
export const customContactNumberValidation = (
  value: any,
  setSupplierContactError: Dispatch<SetStateAction<string | null>>
) => {
  if (value && value.length > 20) {
    setSupplierContactError("Supplier Contact Number must be less than 20 characters!");
    return false;
  }
  setSupplierContactError(null);
  return true;
};

/** Shared source of truth for the Supplier Contact Name rule (required, alphanumeric+space only). Returns the error message, or `null` when valid. */
export const getSupplierContactNameError = (value: any): string | null => {
  const alphanumericRegex = /^[A-Za-z0-9 ]+$/;

  if (!value) {
    return "Supplier Contact Name can’t be empty!";
  }
  if (!alphanumericRegex.test(value)) {
    return "Supplier Contact Name must contain only letters and numbers!";
  }
  return null;
};

export const formatNumber = (value: number | string | undefined): string => {
  if (value == null || isNaN(Number(value))) return "";
  return Number(value).toLocaleString("en-US");
};

export const formatNumberFixed = (n: number) => parseFloat(n?.toFixed(5));

export const checkDateValidation = (data: any) => {
  let currentYear = new Date().getFullYear();
  if (data.length < 4) {
    return true;
  } else if (data.length === 4) {
    if (currentYear >= data) {
      return false;
    } else {
      return true;
    }
  } else {
    let subString = data.substring(0, 4);
    return subString;
  }
};

export const checkValueIsEmpty = (data: any) => {
  if (
    data === null ||
    data === undefined ||
    data === "" ||
    (typeof data === "number" && isNaN(data)) ||
    data === 0
  ) {
    return "warningView";
  }
  return "";
};

export function getDisplayText(
  value?: string | number | null,
  fallback: string = "—"
): { text: string; className: string } {
  if (typeof value === "number") {
    return { text: value.toString(), className: "text-gray-900" };
  }

  if (typeof value !== "string" || value.trim() === "") {
    return { text: fallback, className: "text-red-500" };
  }

  return { text: value, className: "text-gray-900" };
}

export const convertToKg = (size: number, unitType: string): number => {
  switch (unitType?.toLowerCase()) {
    case "g":
      return size / 1000;
    case "ml":
      return size / 1000;
    case "ton":
      return size * 1000;
    case "kg":
    case "l":
    default:
      return size;
  }
};

export const colorMap: Record<number, string> = {
  1: "#131313", // black (colorMap 50; also 52,62)
  2: "#143F76", // navy (colorMap 35)
  3: "#1C7C99", // petrol (colorMap 91)
  4: "#257C38", // forest green (colorMap 56)
  5: "#2583FF", // blue (colorMap 7; also 17,24,38,45,53,70,80,81,82,86,99)
  6: "#2ADD4F", // vivid green (colorMap 78)
  7: "#2EC4B6", // cyan (colorMap 95; also 18,28)
  8: "#30699C", // steel blue (colorMap 31)
  9: "#3D348B", // indigo (colorMap 94)
  10: "#444548", // gray (colorMap 8; also 9,10,25,27,29,32,36,42,46,65,68,72,83,84)
  11: "#46553D", // olive/dark green (colorMap 4; also 21,26,41,44,54,97)
  12: "#46F0F0", // aqua
  13: "#5A7D7D", // teal (colorMap 6; also 13,39,79)
  14: "#6E8AB7", // periwinkle (colorMap 71)
  15: "#756384", // purple (colorMap 22; also 63,67,74)
  16: "#7B3E19", // dark brown (colorMap 92)
  17: "#88BF4C", // lime (colorMap 19; also 47,51,66,75)
  18: "#8EA17B", // sage (colorMap 5; also 14,37,43,61)
  19: "#995D2E", // bronze (colorMap 49)
  20: "#A43D3D", // red (colorMap 3; also 12,30,34,60)
  21: "#B76FDD", // violet (colorMap 89)
  22: "#CD732D", // orange/brown (colorMap 23; also 55,57,73,87)
  23: "#D1495B", // rose (colorMap 98)
  24: "#E4572E", // coral/red-orange (colorMap 90)
  25: "#EF8B8B", // pink (colorMap 11)
  26: "#F032E6", // magenta
  27: "#FBC398", // peach (colorMap 55)
  28: "#FFE388" // yellow/gold (colorMap 1; also 15,16,20,64,76,96)
};

export const getCategoryStyles = (categoryColor: string) => {
  const styles = {
    energy: {
      background: "bg-yellow-500/10",
      icon: "text-yellow-600",
      card: "bg-yellow-500/5"
    },
    waste: {
      background: "bg-green-500/10",
      icon: "text-green-600",
      card: "bg-green-500/5"
    },
    water: {
      background: "bg-blue-500/10",
      icon: "text-blue-600",
      card: "bg-blue-500/5"
    },
    chemicals: {
      background: "bg-purple-500/10",
      icon: "text-purple-600",
      card: "bg-purple-500/5"
    },
    yield: {
      background: "bg-emerald-500/10",
      icon: "text-emerald-600",
      card: "bg-emerald-500/5"
    }
  };
  return styles[categoryColor as keyof typeof styles] || styles.energy;
};

export const downloadFileFromUrl = (
  url: string,
  fileName: string,
  enableCustom: boolean = true
) => {
  const link = document.createElement("a");
  const encodedFileName = encodeURIComponent(fileName);
  // S3 honours the last response-content-disposition, so never override one that is already signed
  const hasSignedDisposition = /[?&]response-content-disposition=/i.test(url);
  link.href =
    enableCustom && !hasSignedDisposition
      ? `${url}&response-content-disposition=attachment%3B%20filename%3D%22${encodedFileName}%22`
      : url;
  link.download = encodedFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface NumberValidationOptions {
  maxValue?: number | string;
  decimalLimit?: number;
  allowEmpty?: boolean;
  maxIntegerDigits?: number;
  allowMinus?: boolean;
}

type ValidationResult = true | string;

// SRP: Each validator has a single, focused responsibility

/**
 * Validates if the value is empty and whether that's allowed
 */
const validateEmptyValue = (
  value: string | number | null | undefined,
  allowEmpty: boolean
): ValidationResult => {
  if (value === "" || value === null || value === undefined) {
    return allowEmpty ? true : "Value is required";
  }
  return true;
};

/**
 * Validates the numeric pattern of a string
 */
const validateNumericPattern = (str: string, allowMinus: boolean): ValidationResult => {
  const pattern = allowMinus ? /^-?\d+(\.\d*)?$/ : /^\d+(\.\d*)?$/;

  if (!pattern.test(str)) {
    return "Only numeric values are allowed";
  }

  if (str.endsWith(".")) {
    return "Please enter digits after the decimal point";
  }

  return true;
};

/**
 * Validates the number of digits before the decimal point
 */
const validateIntegerDigitCount = (integerPart: string, maxDigits?: number): ValidationResult => {
  if (maxDigits !== undefined && integerPart.length > maxDigits) {
    return `Maximum ${maxDigits} digits are allowed before the decimal`;
  }
  return true;
};

/**
 * Validates the number of decimal places
 */
const validateDecimalPlaces = (decimalPart: string, maxPlaces: number): ValidationResult => {
  if (decimalPart.length > maxPlaces) {
    return `Only up to ${maxPlaces} decimal places are allowed`;
  }
  return true;
};

/**
 * Validates if the number is finite
 */
const validateFiniteNumber = (value: number): ValidationResult => {
  if (!Number.isFinite(value)) {
    return "Invalid number";
  }
  return true;
};

/**
 * Validates the number against max/min range constraints
 */
const validateValueRange = (
  value: number,
  maxValue: number | string | undefined,
  decimalLimit: number,
  allowMinus: boolean
): ValidationResult => {
  if (maxValue === undefined) {
    return true;
  }

  const maxVal = typeof maxValue === "string" ? Number(maxValue) : maxValue;

  if (!Number.isFinite(maxVal)) {
    console.error("Invalid maxValue configuration:", maxValue);
    return "Configuration error";
  }

  // Use integer comparison to avoid floating-point precision issues
  const multiplier = Math.pow(10, decimalLimit);
  const valueInt = Math.round(value * multiplier);
  const maxInt = Math.round(maxVal * multiplier);

  if (allowMinus) {
    const minInt = -maxInt;
    if (valueInt > maxInt) {
      return `Maximum allowed value is ${maxValue}`;
    }
    if (valueInt < minInt) {
      return `Minimum allowed value is -${maxValue}`;
    }
  } else {
    if (value < 0) {
      return "Negative values are not allowed";
    }
    if (valueInt > maxInt) {
      return `Maximum allowed value is ${maxValue}`;
    }
  }

  return true;
};

/**
 * Main validator that composes all validation rules
 * Follows the Composition pattern and Chain of Responsibility
 */
export const validateDecimalNumber = (
  value: string | number | null | undefined,
  options: NumberValidationOptions = {}
): ValidationResult => {
  const {
    maxValue,
    decimalLimit = 2,
    allowEmpty = true,
    maxIntegerDigits,
    allowMinus = false
  } = options;

  // Validate empty value
  const emptyValidation = validateEmptyValue(value, allowEmpty);
  if (emptyValidation !== true) return emptyValidation;
  if (value === "" || value === null || value === undefined) return true;

  // Normalize to string
  const str = String(value).trim();
  if (str === "" && allowEmpty) return true;

  // Validate pattern
  const patternValidation = validateNumericPattern(str, allowMinus);
  if (patternValidation !== true) return patternValidation;

  // Parse parts
  const [integerPart, decimalPart = ""] = str.replace(/^-/, "").split(".");

  // Validate integer digit count
  const integerValidation = validateIntegerDigitCount(integerPart, maxIntegerDigits);
  if (integerValidation !== true) return integerValidation;

  // Validate decimal places
  const decimalValidation = validateDecimalPlaces(decimalPart, decimalLimit);
  if (decimalValidation !== true) return decimalValidation;

  // Convert and validate numeric value
  const numericValue = Number(str);
  const finiteValidation = validateFiniteNumber(numericValue);
  if (finiteValidation !== true) return finiteValidation;

  // Validate range
  const rangeValidation = validateValueRange(numericValue, maxValue, decimalLimit, allowMinus);
  if (rangeValidation !== true) return rangeValidation;

  return true;
};

export const validateProductNumber = (v: any) => {
  return validateDecimalNumber(v, { maxValue: 99999.99, decimalLimit: 2 });
};

export const validateFacilityNumber = (v: any) => {
  return validateDecimalNumber(v, { maxValue: "9999999.99999", decimalLimit: 5 });
};

export const validateIngredientUnit = (v: any) => {
  return validateDecimalNumber(v, { maxValue: "99999.99", decimalLimit: 2 });
};

export const validateExternalStorageName = (v: any, allowMinus: boolean = false) => {
  return validateDecimalNumber(v, {
    maxValue: "999999999.999999999999",
    decimalLimit: 12,
    allowMinus
  });
};

export const validateExternalTransport = (v: any) => {
  return validateDecimalNumber(v, { maxValue: "999999999.999999999999", decimalLimit: 12 });
};

export const validateCultivationNumber = (v: any) => {
  return validateDecimalNumber(v, {
    maxIntegerDigits: 10,
    decimalLimit: 12,
    allowEmpty: true
  });
};

export const validatePercentageNumber = (v: any) => {
  return validateDecimalNumber(v, {
    maxIntegerDigits: 3,
    decimalLimit: 12,
    maxValue: 100,
    allowEmpty: true
  });
};

export const hasValidationErrors = (
  ...errorObjects: Array<Record<string, any> | undefined | null>
): boolean => {
  return errorObjects.some(err => !!err && Object.keys(err).length > 0);
};

export const formatDecimalValue = (value: number | undefined | null) => {
  if (value == null) return "";
  const rounded = Number(value.toFixed(5));
  // Split integer and decimal
  const parts = rounded.toString().split(".");
  const decPart = parts[1];
  if (!decPart) return rounded.toFixed(2); // default 2 decimals
  return rounded.toString(); // show actual decimals without trailing zeros
};

export const validateCharacterLength = (
  value: string,
  maxLength: number,
  customMessage?: string
) => {
  if (value.length > maxLength) {
    return customMessage || `Maximum ${maxLength} characters allowed`;
  }
  return true;
};

// Measurement fields validation: 10 digits before decimal, 12 after
export const validateMeasurementField = (value: any, allowEmpty: boolean = true) => {
  return validateDecimalNumber(value, {
    maxValue: "9999999999.999999999999",
    decimalLimit: 12,
    allowEmpty
  });
};

// Recipe value %: 3 digits before, 12 after, max 999.999999999999
export const validateRecipeValuePercent = (value: any) => {
  const result = validateDecimalNumber(value, {
    decimalLimit: 12,
    maxValue: "999.999999999999",
    allowEmpty: true
  });
  return result;
};

// Heat source amount: 10 digits before, 12 after (required)
export const validateHeatSourceAmount = (value: any) => {
  return validateDecimalNumber(value, {
    maxValue: "9999999999.999999999999",
    decimalLimit: 12,
    allowEmpty: false
  });
};

// Water source amount: 10 digits before, 12 after (required)
export const validateWaterSourceAmount = (value: any) => {
  return validateDecimalNumber(value, {
    maxValue: "9999999999.999999999999",
    decimalLimit: 12,
    allowEmpty: false
  });
};

// CIP/SIP Frequency: integer only, 0-999
export const validateCIPSIPFrequency = (value: any) => {
  if (value === "" || value === null || value === undefined) {
    return true;
  }

  const numValue = Number(value);

  if (isNaN(numValue)) {
    return "Invalid number";
  }

  if (numValue < 0) {
    return "Value must be 0 or greater";
  }

  if (numValue > 999) {
    return "Maximum value is 999";
  }

  if (!Number.isInteger(numValue)) {
    return "Decimals are not allowed";
  }

  return true;
};

// CIP/SIP numeric fields: 10 digits before, 12 after
export const validateCIPSIPNumericField = (value: any) => {
  return validateDecimalNumber(value, {
    maxValue: "9999999999.999999999999",
    decimalLimit: 12,
    maxIntegerDigits: 10,
    allowEmpty: true
  });
};

// Chemical amount: 10 digits before, 12 after
export const validateChemicalAmount = (value: any) => {
  return validateDecimalNumber(value, {
    maxValue: "9999999999.999999999999",
    decimalLimit: 12,
    allowEmpty: true
  });
};

// Waste moisture content: 3 digits before, 12 after
export const validateMoistureContent = (value: any) => {
  return validateDecimalNumber(value, {
    maxValue: "999.999999999999",
    decimalLimit: 12,
    allowEmpty: true
  });
};

// Waste distribution: 10 digits before, 12 after
export const validateDistribution = (value: any) => {
  return validateDecimalNumber(value, {
    maxValue: "9999999999.999999999999",
    decimalLimit: 12,
    allowEmpty: true
  });
};

// --- Recipe Simulator: Heating/Cooling Temperature Validations ---

export const validateHeatingTemperatures = (
  startingTemp: number | null,
  targetTemp: number | null
): true | string => {
  if (startingTemp === null || targetTemp === null) return true;
  if (targetTemp <= startingTemp) {
    return "Target Temperature must be greater than Starting Temperature for heating";
  }
  return true;
};

export const validateCoolingTemperatures = (
  startingTemp: number | null,
  targetTemp: number | null
): true | string => {
  if (startingTemp === null || targetTemp === null) return true;
  if (startingTemp <= targetTemp) {
    return "Starting Temperature must be greater than Target Temperature for cooling";
  }
  return true;
};

export const validateCoolingMethodMinTemp = (
  method: number | string | undefined | null,
  targetTemp: number | null,
  coolingMethodMinTemps: Record<string, number>,
  coolingMethodOptions: Array<{ value: CoolingMethodOption; text: string }>
): true | string => {
  if (!method || targetTemp === null) return true;
  const minTemp = coolingMethodMinTemps[method];
  if (minTemp === undefined) return true;
  if (targetTemp < minTemp) {
    const methodLabel = coolingMethodOptions.find(opt => opt.value === method)?.text || method;
    return `Target rejected. ${methodLabel} target cannot be below ${minTemp}°C.`;
  }
  return true;
};

export const validateInternalStorage = (v: any, allowMinus: boolean = false) => {
  return validateDecimalNumber(v, {
    maxValue: "999999999.999999999999",
    decimalLimit: 12,
    allowMinus
  });
};

export const validateInternalTransport = (v: any) => {
  return validateDecimalNumber(v, { maxValue: "999999999.999999999999", decimalLimit: 12 });
};

export const formatWeightWithUnit = (value: number, targetUnit: "kg" | "ton") =>
  targetUnit === "ton" ? value.toFixed(3) : value.toFixed(2);

/**
 * Convert numeric unit (1, 2) to string unit ("kg", "ton")
 * Handles undefined/null, string, and numeric inputs robustly
 */
export const convertNumericUnitToString = (numericUnit: number | string | undefined): string => {
  const WEIGHT_KG = 1;
  const WEIGHT_TON = 2;

  // Handle undefined/null
  if (numericUnit === undefined || numericUnit === null) return "kg";

  // If it's a string, check if it's already "kg" or "ton"
  if (typeof numericUnit === "string") {
    if (numericUnit === "kg" || numericUnit === "ton") return numericUnit;

    // Try to parse string as number (e.g., "1" or "2")
    const parsed = parseInt(numericUnit, 10);
    if (!isNaN(parsed)) {
      if (parsed === WEIGHT_KG || parsed === 1) return "kg";
      if (parsed === WEIGHT_TON || parsed === 2) return "ton";
    }

    // Invalid string, default to kg
    return "kg";
  }

  // Convert numeric values
  if (numericUnit === WEIGHT_KG || numericUnit === 1) return "kg";
  if (numericUnit === WEIGHT_TON || numericUnit === 2) return "ton";

  // Unknown numeric value, default to kg
  return "kg";
};

/**
 * Convert string unit ("kg", "ton") to numeric unit (1, 2)
 * Mirrors convertNumericUnitToString for parent form sync.
 */
export const convertStringUnitToNumeric = (stringUnit: string | undefined): number => {
  const WEIGHT_KG = 1;
  const WEIGHT_TON = 2;

  if (stringUnit === "ton") return WEIGHT_TON;
  return WEIGHT_KG;
};

/** Expands scientific notation (e.g. 1e-10, 1.5e+21) into a plain decimal string so digit filtering doesn't mangle it. */
export const expandExponent = (input: string): string => {
  const match = /^(-?)(\d*)\.?(\d*)e([+-]?\d+)$/i.exec(input.trim());
  if (!match) return input;
  const [, sign, intPart, fracPart, exp] = match;
  const digits = intPart + fracPart;
  const pointIndex = intPart.length + parseInt(exp, 10);
  let result: string;
  if (pointIndex <= 0) {
    result = `0.${"0".repeat(-pointIndex)}${digits}`;
  } else if (pointIndex >= digits.length) {
    result = digits + "0".repeat(pointIndex - digits.length);
  } else {
    result = `${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`;
  }
  return sign + result;
};
